"use client";

import MainServiceItem from "./MainServiceItem";
import IntegrationPartnerModal from "./integration-partner/IntegrationPartnerModal";
import { memo, useCallback } from "react";
import { useSetAtom } from "jotai";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useHeroIntegrationPartner } from "./integration-partner/useHeroIntegrationPartner";
import { useServiceComingSoon } from "@/hooks/config/useWebConfig";
import { toSearchServiceKey } from "@/components/search-form/search.modules.type";
import { dialogAtom } from "@/stores/dialogAtom";
import {
  heroServiceItems,
  isIntegrationMainServiceItem,
  isInternalMainServiceItem,
  type MainServiceItemData,
} from "./mainServiceItems";

type MainServicesProps = {
  selectedService?: string;
  onSelectService: (service: string) => void;
};

const MainServices = (props: MainServicesProps) => {
  const { selectedService, onSelectService } = props;
  const setDialog = useSetAtom(dialogAtom);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { badgeByServiceKey, isServiceComingSoon } = useServiceComingSoon();

  const {
    activeIntegrationKey,
    closePartnerModal,
    isOpeningIntegrationLink,
    startPartnerRedirectFlow,
    partnerDialogState,
    retryPartnerRedirectFlow,
  } = useHeroIntegrationPartner();

  const handleServiceClick = useCallback(
    async (item: MainServiceItemData) => {
      if (isServiceComingSoon(item)) {
        setDialog({
          id: "coming_soon_dialog",
          props: { type: item.key },
        });
        return;
      }

      if (isInternalMainServiceItem(item)) {
        if (item.selectValue) {
          onSelectService(item.selectValue);
        }
        return;
      }
      if (
        isIntegrationMainServiceItem(item) &&
        !isOpeningIntegrationLink(item.integrationKey)
      ) {
        await startPartnerRedirectFlow(item.integrationKey);
      }
    },
    [
      isOpeningIntegrationLink,
      isServiceComingSoon,
      onSelectService,
      setDialog,
      startPartnerRedirectFlow,
    ],
  );

  const currentServiceKey = toSearchServiceKey(selectedService);

  return (
    <>
      <div className="w-full md:absolute md:left-1/2 md:-top-[10px] md:max-w-[760px] md:-translate-x-1/2 md:-translate-y-[38%]">
        <div className="grid grid-cols-3 gap-4 md:grid-cols-6 lg:gap-3">
          {heroServiceItems.map((item) => {
            const isSelected =
              !isMobile && isInternalMainServiceItem(item)
                ? currentServiceKey === toSearchServiceKey(item.selectValue)
                : false;
            const isLoading = isIntegrationMainServiceItem(item)
              ? isOpeningIntegrationLink(item.integrationKey)
              : false;
            const isComingSoon = isServiceComingSoon(item);

            return (
              <MainServiceItem
                key={item.key}
                item={item}
                badge={
                  item.remoteConfigKey
                    ? badgeByServiceKey.get(item.remoteConfigKey)
                    : undefined
                }
                isMobile={isMobile}
                isSelected={isSelected}
                isLoading={isLoading}
                isComingSoon={isComingSoon}
                onClick={handleServiceClick}
              />
            );
          })}
        </div>
      </div>

      <IntegrationPartnerModal
        open={partnerDialogState !== "closed"}
        integrationKey={activeIntegrationKey}
        state={partnerDialogState === "error" ? "error" : "loading"}
        onClose={closePartnerModal}
        onRetry={retryPartnerRedirectFlow}
      />
    </>
  );
};

export default memo(MainServices);
